import { useEffect, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { socket } from "src/utils/socket";
import axios from "src/utils/axios";
import { store, useDispatch } from "src/redux/store";
import { useSnackbar } from "src/components/snackbar";
import { useAuthContext } from "src/auth/useAuthContext";
import { getOrderAndDraftCount } from "src/redux/slices/orderAndDraftCountSlice";
import { autoPrint } from "src/utils/printToIP";
import { filterOutVoidItems } from "src/utils/filterVoidItems";
import {
  getDefaultReceipt,
  getDefaultPrinter,
  getPrintersForReceipts,
  getFailedPrinterNames,
  hasStationDataChanged,
} from "src/utils/posLayout/classicalPOSReceiptUtils";
import {
  KITCHEN_RECEIPT_MASTER_ACCOUNT_ORDER_EVENT,
  KITCHEN_RECEIPT_MASTER_ACCOUNT_OPEN_ORDERS_REFRESH,
  runKitchenReceiptMasterAccountExclusive,
  getOrderVersionKey,
  isOrderVersionProcessed,
  markOrderVersionProcessed,
  saveOrderSnapshot,
  getItemsForStationChangeCheck,
} from "src/utils/posLayout/kitchenReceiptMasterAccountUtils";
import { KitchenReceiptThroughIPAutoStation } from "src/sections/@dashboard/pos/invoice/KitchenReceiptThroughIPAutoStation";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const getEntityId = (value) => String(value?.id || value?._id || value || "");

const getSelectedLevelId = (user) => {
  try {
    return JSON.parse(sessionStorage.getItem("levelSelected"))?.id || user?.levelId;
  } catch (error) {
    return user?.levelId;
  }
};

export default function GlobalKitchenReceiptMasterAccountListener() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (!user?.id) return undefined;

    const showPrintResults = (results, printers) => {
      const successResult = results.find((res) => res.success);
      const failedIPs = results.filter((res) => !res.success).map((res) => `${res.ipAddress}`);

      if (successResult) {
        enqueueSnackbar(successResult.message, { variant: "success" });
      }
      if (failedIPs.length > 0) {
        enqueueSnackbar(`Print Failed\n${getFailedPrinterNames(printers, failedIPs).join(",\n")}`, {
          variant: "error",
          autoHideDuration: 6000,
          style: { whiteSpace: "pre-line" },
        });
      }
    };

    const printOrder = async (order, action) => {
      const currentUser = userRef.current;
      const { receiptPrinterIPs = [] } = currentUser?.venue ?? {};
      const orderVenueId = getEntityId(order.venueId);
      const orderLevelId = getEntityId(order.levelId);

      // Kitchen stations belong to the order's level, which may differ from the level selected here.
      const receiptResponse = await axios.get(
        `${backendUrl}/venueReceipt?venueId=${orderVenueId}&levelId=${orderLevelId}`
      );
      const receiptList = receiptResponse?.data?.results || [];

      const orderItems = order.items || [];
      const nonVoidedItems = filterOutVoidItems(orderItems);
      let printers;

      if (action === "updated") {
        const changeCheckItems = getItemsForStationChangeCheck(order);
        printers = getPrintersForReceipts(receiptList, receiptPrinterIPs, orderItems).filter((printer) =>
          hasStationDataChanged(printer.receipt.categories, changeCheckItems)
        );
      } else {
        printers = getPrintersForReceipts(receiptList, receiptPrinterIPs, nonVoidedItems);

        // Same fallback as the POS add-order flow: no station printer matched, use the default receipt printer.
        if (printers.length === 0 && nonVoidedItems.length > 0) {
          const defaultReceipt = getDefaultReceipt(receiptList);
          const defaultPrinter = getDefaultPrinter(receiptPrinterIPs, defaultReceipt);
          if (defaultPrinter?.ipAddress) {
            printers = [{
              receipt: { ...defaultReceipt, categories: [] },
              ipAddress: defaultPrinter.ipAddress,
              port: defaultPrinter.port || 9100,
            }];
          }
        }
      }

      if (printers.length === 0) return;

      const defaultReceipt = getDefaultReceipt(receiptList);
      const { venueAllUser = [] } = store.getState()?.venueUser ?? {};
      const { tables = [] } = store.getState()?.table ?? {};
      const results = [];

      for (const printer of printers) {
        try {
          const receiptHTML = ReactDOMServer.renderToStaticMarkup(
            <KitchenReceiptThroughIPAutoStation
              openDefault={defaultReceipt ? printer.receipt._id === defaultReceipt._id : false}
              user={currentUser}
              venueAllUser={venueAllUser}
              tables={tables}
              posCart={order}
              selectedCategories={{
                categories: printer.receipt.categories || [],
                name: printer.receipt.name || "",
              }}
              receiptList={receiptList}
              cartType={order.orderType || order.cartType || ""}
              name={order.label || ""}
              customer={order.customer || order.customerObject}
              assignee={getEntityId(order.assignee)}
              tableNumber={order.tableNumber}
              tableID={getEntityId(order.tableId)}
            />
          );

          const res = await autoPrint({
            receiptData: receiptHTML,
            ipAddress: printer.ipAddress,
            port: printer.port,
          });
          results.push({ ...res, ipAddress: printer.ipAddress });

          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (printError) {
          results.push({ success: false, message: printError.message, ipAddress: printer.ipAddress });
        }
      }

      showPrintResults(results, printers);
    };

    const handleMasterAccountOrder = ({ venueId, orderId, action } = {}) => {
      const currentUser = userRef.current;
      if (!orderId || !currentUser?.id) return;
      if (String(venueId) !== getEntityId(currentUser.venueId || currentUser.venue?.id)) return;

      runKitchenReceiptMasterAccountExclusive(async () => {
        try {
          const response = await axios.get(`${backendUrl}/orders/${orderId}`);
          const order = response?.data;
          if (!order) return;

          const versionKey = getOrderVersionKey(order);
          if (isOrderVersionProcessed(versionKey)) return;
          markOrderVersionProcessed(versionKey);

          // Existing Open Orders refresh: server counts plus the POS list reload in CheckoutCart.
          dispatch(getOrderAndDraftCount(currentUser.venue?.id, getSelectedLevelId(currentUser)));
          window.dispatchEvent(
            new CustomEvent(KITCHEN_RECEIPT_MASTER_ACCOUNT_OPEN_ORDERS_REFRESH, { detail: { orderId, action } })
          );

          // This account saved the order itself, so its own POS flow already handled the kitchen slip.
          const isSavedByThisAccount = getEntityId(order.venueUserId) === String(currentUser.id);

          if (!isSavedByThisAccount) {
            await printOrder(order, action);
          }

          saveOrderSnapshot(order);
        } catch (error) {
          console.error("Kitchen receipt master account order failed:", error);
          enqueueSnackbar("Unable to auto print kitchen receipt for the latest order", { variant: "error" });
        }
      });
    };

    socket.on(KITCHEN_RECEIPT_MASTER_ACCOUNT_ORDER_EVENT, handleMasterAccountOrder);
    return () => {
      socket.off(KITCHEN_RECEIPT_MASTER_ACCOUNT_ORDER_EVENT, handleMasterAccountOrder);
    };
  }, [user?.id, dispatch, enqueueSnackbar]);

  return null;
}
